// Keeps phone forms above the on-screen keyboard.
//
// The store app resizes the web view when the keyboard opens (Capacitor
// Keyboard plugin), but a phone *browser* usually does not: Chrome on Android
// and Safari on iOS lay the keyboard over the page, so a bottom sheet's reason
// box and its Save button — both pinned to the bottom edge — end up under it.
//
// visualViewport is the part of the page the keyboard leaves visible. The gap
// between it and the layout viewport is the keyboard, published as the CSS
// variable --kb-inset, which the phone block in index.css uses to lift bottom
// sheets and shorten full-screen forms. Where the view is resized instead, the
// two viewports agree and the inset stays 0, so nothing is lifted twice.
//
// A field that gets focus inside a sheet or a full-screen form is also scrolled
// to the middle of what is left, once the keyboard has finished opening.
const PHONE = 'screen and (max-width: 767.98px)';
const CONTAINERS = '[role="dialog"], .m3-screen, [data-vaul-drawer]';

function isTypingField(el) {
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (!(el instanceof HTMLInputElement)) return false;
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color']
    .includes(el.type);
}

export function installKeyboardInset() {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const root = document.documentElement;
  const vv = window.visualViewport;

  const update = () => {
    let inset = 0;
    // Pinch-zoom also shrinks the visual viewport; that is not a keyboard.
    if (vv && window.matchMedia(PHONE).matches && vv.scale <= 1.01) {
      inset = Math.max(0, Math.round(root.clientHeight - (vv.height + vv.offsetTop)));
      // Browser chrome sliding away is a few dozen pixels; a keyboard is not.
      if (inset < 80) inset = 0;
    }
    root.style.setProperty('--kb-inset', `${inset}px`);
  };

  const onFocus = (e) => {
    const el = e.target;
    if (!isTypingField(el) || !window.matchMedia(PHONE).matches) return;
    if (!el.closest(CONTAINERS)) return;
    // After the keyboard animation, or the scroll is measured against the
    // full-height view and lands the field under the keyboard anyway.
    setTimeout(() => {
      if (document.activeElement !== el) return;
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* old WebView */ }
    }, 320);
  };

  update();
  vv?.addEventListener('resize', update);
  vv?.addEventListener('scroll', update);
  window.addEventListener('resize', update);
  document.addEventListener('focusin', onFocus);
  return () => {
    vv?.removeEventListener('resize', update);
    vv?.removeEventListener('scroll', update);
    window.removeEventListener('resize', update);
    document.removeEventListener('focusin', onFocus);
    root.style.removeProperty('--kb-inset');
  };
}
